import React, { useCallback, useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { trackEvent } from 'utils/analytics';
import { SpotInterface } from 'types';
import { RevenueRentalType } from 'types/TransactionReport';
import { ChartId, OnOffOption, Period, RevenueType, Timeframe } from 'components/RevenueWidgets/helpers';
import Loader from 'components/Loader/Loader';
import Button from 'components/Button/Button';
import AGMessage from 'components/AGMessage/AGMessage';
import DownloadIcon from 'assets/icons/download.svg?react';
import styles from './PDFReportPreview.module.css';
import CoverPage from './PDFpages/CoverPage';
import RevenuePage from './PDFpages/RevenuePage';
import ByRentalTypePage from './PDFpages/ByRentalTypePage';
import HistoricalPage from './PDFpages/HistoricalPage';
import PacingPage from './PDFpages/PacingPage';

type PDFReportPreviewProps = {
    spot: SpotInterface;
    filters: {
        revenueType: RevenueType;
        timeframe: Timeframe;
        dailyPeriod: Period;
        rentalType: RevenueRentalType;
        yoyRevenueType: RevenueType;
        month: string;
        momYearCompare: OnOffOption;
        momGuaranteeCompare: OnOffOption;
    };
    onClose: () => void;
};

type CapturedCharts = {
    [ChartId.REVENUE_CHART]: HTMLCanvasElement | null;
    [ChartId.RENTAL_TYPE_CHART]: HTMLCanvasElement | null;
    [ChartId.HISTORICAL_CHART]: HTMLCanvasElement | null;
    [ChartId.PACING_CHART]: HTMLCanvasElement | null;
};

const dateTime = new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});

function PDFReportPreview({ spot, filters, onClose }: PDFReportPreviewProps): JSX.Element {
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [capturedCharts, setCapturedCharts] = useState<CapturedCharts>({
        [ChartId.REVENUE_CHART]: null,
        [ChartId.RENTAL_TYPE_CHART]: null,
        [ChartId.HISTORICAL_CHART]: null,
        [ChartId.PACING_CHART]: null,
    });

    const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

    const captureChartsFromDOM = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');

        try {
            const charts: CapturedCharts = {
                [ChartId.REVENUE_CHART]: null,
                [ChartId.RENTAL_TYPE_CHART]: null,
                [ChartId.HISTORICAL_CHART]: null,
                [ChartId.PACING_CHART]: null,
            };

            for (const chartId of Object.values(ChartId)) {
                const chartElement = document.getElementById(chartId);

                if (chartElement) {
                    const canvas = await html2canvas(chartElement, {
                        scale: 2,
                        useCORS: true,
                        backgroundColor: '#ffffff',
                        logging: false,
                    });
                    charts[chartId] = canvas;
                }
            }

            setCapturedCharts(charts);
        } catch (e) {
            setErrorMessage('Failed to capture charts. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        captureChartsFromDOM();
    }, [captureChartsFromDOM]);

    const downloadPDF = async () => {
        setDownloading(true);

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            for (let i = 0; i < pageRefs.current.length; i++) {
                const pageElement = pageRefs.current[i];
                if (!pageElement) continue;

                const canvas = await html2canvas(pageElement, {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    logging: false,
                });

                const imgData = canvas.toDataURL('image/png');

                if (i > 0) pdf.addPage();

                // Calculate dimensions to maintain aspect ratio and fit page
                const canvasAspectRatio = canvas.width / canvas.height;
                const pageAspectRatio = pageWidth / pageHeight;

                let imgWidth: number;
                let imgHeight: number;

                if (canvasAspectRatio > pageAspectRatio) {
                    // Canvas is wider - fit to width, scale height
                    imgWidth = pageWidth;
                    imgHeight = pageWidth / canvasAspectRatio;
                } else {
                    // Canvas is taller - fit to height, scale width
                    imgHeight = pageHeight;
                    imgWidth = pageHeight * canvasAspectRatio;
                }

                pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
            }

            const fileName = `${spot.name}_Revenue_Insights_${new Date().toISOString().split('T')[0]}.pdf`.replace(
                / /g,
                '_'
            );
            pdf.save(fileName);

            trackEvent('Download Report', {
                spot: spot.pk,
                spotName: spot.name,
                type: 'pdf',
                params: {
                    revenueType: filters.revenueType,
                    timeframe: filters.timeframe,
                    dailyPeriod: filters.dailyPeriod,
                    rentalType: filters.rentalType,
                    yoyRevenueType: filters.yoyRevenueType,
                    month: filters.month,
                },
            });

            onClose();
        } catch (e) {
            setErrorMessage('Failed to generate PDF. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const setPageRef = (index: number) => (el: HTMLDivElement | null) => {
        pageRefs.current[index] = el;
    };

    const spotAddress = spot.address
        ? `${spot.address.address_line1}, ${spot.address.city}, ${spot.address.state}`
        : spot.name;

    return (
        <div>
            {errorMessage && <AGMessage color="error">{errorMessage}</AGMessage>}

            <div className={styles.scrollablePreview}>
                {loading ? (
                    <Loader centered />
                ) : (
                    <div className={styles.pagesContainer}>
                        <CoverPage ref={setPageRef(0)} spotAddress={spotAddress} />

                        <RevenuePage
                            ref={setPageRef(1)}
                            spotAddress={spotAddress}
                            dateTime={dateTime}
                            chartCanvas={capturedCharts[ChartId.REVENUE_CHART]}
                            filters={filters}
                        />

                        <ByRentalTypePage
                            ref={setPageRef(2)}
                            spotAddress={spotAddress}
                            dateTime={dateTime}
                            chartCanvas={capturedCharts[ChartId.RENTAL_TYPE_CHART]}
                            filters={filters}
                        />

                        <HistoricalPage
                            ref={setPageRef(3)}
                            spotAddress={spotAddress}
                            dateTime={dateTime}
                            chartCanvas={capturedCharts[ChartId.HISTORICAL_CHART]}
                            filters={filters}
                        />

                        <PacingPage
                            ref={setPageRef(4)}
                            spotAddress={spotAddress}
                            dateTime={dateTime}
                            chartCanvas={capturedCharts[ChartId.PACING_CHART]}
                        />
                    </div>
                )}
            </div>

            <div className={styles.downloadButtonContainer}>
                <Button onClick={downloadPDF} loading={downloading} disabled={loading} size="md">
                    <DownloadIcon /> Download PDF
                </Button>
            </div>
        </div>
    );
}

export default PDFReportPreview;
